import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";

import {
  locationSalesGetOrderTypeReportData,
  type OrderTypeEntry,
} from "~/api-refs";
import { ReportFilterState } from "~/components/reports/report-filter-state";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";
import { useSearchParamsDateRange } from "~/utils/search-params-date-range";

export const Route = createFileRoute(
  "/_authenticated/reports/detailed-order-type-report"
)({
  component: DetailedOrderTypeReportPage,
});

type Data = OrderTypeEntry;

function DetailedOrderTypeReportPage() {
  const searchParams = useSearch({ strict: false });
  const locationId = searchParams.location;

  if (!locationId) {
    return (
      <ReportFilterState
        message="Please select a location from the filters above to view Detailed Order Type Report."
        title="Location Required"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="subtitle text-foreground mb-2">
          Detailed Order Type Report
        </h2>
        <p className="body text-muted-foreground">
          View order type analysis and performance data
        </p>
      </div>

      <DetailedOrderTypeReportContent locationId={locationId} />
    </div>
  );
}

const columnHelper = createColumnHelper<Data>();

const columns = [
  columnHelper.accessor("location", {
    header: "Location Name",
    cell: ({ getValue }) => getValue().name,
    filterFn: ({ original }, _, filterValue: string[]) =>
      filterValue.includes(original.location.id),
    meta: {
      faceting: {
        title: "Location",
        getOption: (r: Data["location"]) => ({
          value: r.id,
          label: r.name,
        }),
      },
      getCellForExport: (r: Data["location"]) => r.name,
    },
  }),
  columnHelper.accessor("type", {
    header: "Order Type",
    meta: {
      faceting: {
        title: "Order Type",
        getOption: (t: Data["type"]) => ({
          value: t,
          label: t,
        }),
      },
    },
  }),
  columnHelper.accessor("itemsSold", {
    header: "Items Sold",
    cell: ({ getValue }) => getValue()?.toLocaleString("en-US"),
    meta: {
      getCellForExport: (d: Data["itemsSold"]) => d,
    },
  }),
  columnHelper.accessor("amountSold", {
    header: "Amount Sold",
    cell: ({ getValue }) =>
      getValue()?.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      }),
    meta: {
      getCellForExport: (d: Data["amountSold"]) => d,
    },
  }),
] as ColumnDef<Data>[];

const DetailedOrderTypeReportContent = ({
  locationId,
}: {
  locationId: string;
}) => {
  const [dateRange] = useSearchParamsDateRange();

  const { data, isPending } = useQuery(
    convexQuery(locationSalesGetOrderTypeReportData, {
      dateRange: {
        from: dateRange.from?.getTime() ?? 0,
        to: dateRange.to?.getTime() ?? 0,
      },
      locationId: toConvexTableId("locations", locationId),
    })
  );

  if (isPending) {
    return <LoadingPanel message="Loading order type data..." />;
  }

  return (
    <SalesDataTable
      columns={columns}
      data={data ?? []}
      id="detailed-order-type-report-table"
    />
  );
};
